import { useCallback, useMemo } from 'react';
import { host } from '@branding/config';
import { INVESTIGATION_ANSWERED_BY } from '../types/GeneralTypes';
import type {
	V2AnswerPagePayload,
	V2AnswerPageResponse,
	V2BackResponse,
	V2ErrorBody,
	V2ErrorCode,
	V2InvestigationDetails,
	V2Page,
	V2UploadResponse,
	V2ValidationErrorEntry,
} from '../types/V2Types';

/**
 * Thrown by `useQueryAnswerPage` whenever the BE returns a non-2xx with the
 * `V2ErrorBody` envelope (or our best-guess fallback). The context layer
 * pattern-matches on `errorCode` to surface the right user message.
 */
export class V2RequestError extends Error {
	public readonly errorCode: V2ErrorCode;
	public readonly status: number;
	public readonly body: V2ErrorBody;

	constructor(status: number, body: V2ErrorBody) {
		super(body.message || body.errorCode);
		this.name = 'V2RequestError';
		this.status = status;
		this.errorCode = body.errorCode;
		this.body = body;
	}
}

/**
 * BE wire envelope (see `packages/lib-containers/src/fastify/pluginRequestInit.ts`):
 *   { status: "error", httpCode: 400, refineErrorCode: 2005, message: "...",
 *     traceId: "...", body?: { validationErrors?: V2ValidationErrorEntry[] } }
 *
 * We translate that into the FE-native `V2ErrorBody` (string-keyed `errorCode`)
 * so the rest of the FE doesn't have to know about numeric refine codes. MSW
 * test handlers may still emit the older `{ errorCode: "...", message: "..." }`
 * shape directly — that branch is preserved.
 */
async function parseErrorBody(response: Response): Promise<V2ErrorBody> {
	try {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const json = (await response.json()) as any;

		// Path A: MSW / FE-shaped envelope. The reason may sit on the top-level
		// envelope or inside its `body` — surface either (RND-4283).
		if (json && typeof json.errorCode === 'string' && typeof json.message === 'string') {
			const reasonA = typeof json.reason === 'string'
				? json.reason
				: typeof json.body?.reason === 'string'
					? json.body.reason
					: undefined;
			return { ...json, ...(reasonA ? { reason: reasonA } : {}) } as V2ErrorBody;
		}

		// Path B: real BE envelope — translate numeric `refineErrorCode` → string code
		// and lift the `validationErrors` array out of the nested `body` object so
		// the context/renderer layers can consume them without reaching deeper.
		const beCode = typeof json?.refineErrorCode === 'number' ? json.refineErrorCode : undefined;
		const beMessage = typeof json?.message === 'string' ? json.message : `HTTP ${response.status}`;
		const beBody = json && typeof json.body === 'object' ? json.body : undefined;
		const beValidationErrors: V2ValidationErrorEntry[] | undefined = Array.isArray(beBody?.validationErrors)
			? (beBody!.validationErrors as V2ValidationErrorEntry[])
			: undefined;
		// Lift the BE disambiguator `body.reason` to a first-class field so the
		// context can route caseTerminal → closed screen WITHOUT re-submitting
		// (RND-4283). tokenStale / reason-less 409s fall through unchanged.
		const beReason: string | undefined = typeof beBody?.reason === 'string' ? beBody.reason : undefined;

		const errorCode: V2ErrorCode = mapRefineErrorCode(beCode, response.status);

		return {
			errorCode,
			message: beMessage,
			details: json,
			...(beValidationErrors ? { validationErrors: beValidationErrors } : {}),
			...(beReason ? { reason: beReason } : {}),
		};
	} catch {
		return {
			errorCode: inferErrorCode(response.status),
			message: `HTTP ${response.status}`,
		};
	}
}

/**
 * Map numeric `refineErrorCode` → FE-native string code. Falls back to HTTP-
 * status inference if the BE didn't return a recognised code.
 *
 * Codes mirror `packages/lib-core/src/common/refineErrors.ts`:
 *   2005  → PageValidationRefineError   (HTTP 400)
 *   5000  → NotFoundRefineError         (HTTP 400)
 *   5001  → ShortCodeNotFoundRefineError (HTTP 500)
 *   11003 → BackNotPermittedRefineError (HTTP 400)
 *   (StaleClientState block is in the 11xxx range too)
 */
function mapRefineErrorCode(refineErrorCode: number | undefined, status: number): V2ErrorCode {
	switch (refineErrorCode) {
		case 2005:
			return 'PageValidationRefineError';
		case 5000:
			return 'NotFoundRefineError';
		case 5001:
			return 'ShortCodeNotFoundRefineError';
		case 11003:
			return 'BackNotPermittedRefineError';
		default:
			return inferErrorCode(status);
	}
}

function inferErrorCode(status: number): V2ErrorCode {
	if (status === 409) return 'StaleClientStateRefineError';
	// 401/403 = the short-lived link is no longer valid (expired / revoked). On
	// the boot path this drives the dedicated `hrr-expired` screen; in-page it
	// stays an inline recoverable banner (RND-4283).
	if (status === 401 || status === 403) return 'ExpiredLinkRefineError';
	if (status === 400) return 'BackNotPermittedRefineError';
	if (status === 404) return 'NotFoundRefineError';
	if (status === 422) return 'ValidationRefineError';
	return 'UnknownError';
}

async function asJson<T>(response: Response): Promise<T> {
	if (!response.ok) {
		throw new V2RequestError(response.status, await parseErrorBody(response));
	}
	return (await response.json()) as T;
}

/**
 * Unwrap a BE localizable-text value to a plain display string.
 *
 * The BE serves most copy (`label`, `helpText`, option `label`s) already
 * resolved to plain strings, but `static_text.text` arrives as a discriminated
 * object — `{ format: 'literal', value }` after server-side translation, or the
 * un-translated `{ format: 'i18nKey', key }` as a defensive fallback. Rendering
 * that object straight into JSX is what produced the blank-page crash
 * ("Objects are not valid as a React child (found: object with keys
 * {format, value})"). Always funnel any field that may carry the object shape
 * through here so a string reaches the renderers.
 */
function unwrapLocalizableText(input: unknown): string {
	if (input == null) return '';
	if (typeof input === 'string') return input;
	if (typeof input === 'object') {
		const obj = input as { value?: unknown; key?: unknown };
		if (typeof obj.value === 'string') return obj.value;
		if (typeof obj.key === 'string') return obj.key;
	}
	return '';
}

/** Map BE element kinds → PQ V2ElementType names. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function adaptElement(el: any): any {
	const kindMap: Record<string, string> = {
		text: 'text',
		textarea: 'text',     // map textarea → text (PQ uses TextElement for both)
		radio_group: 'singleSelect',
		select: 'dropdown',   // BE kind "select" → MUI <TextField select> dropdown (per Figma slides 85–87)
		checkbox_group: 'multiSelect',
		checkbox: 'attestation',
		number: 'numeric',
		date: 'date',
		file_upload: 'fileUpload',
		static_text: 'infoBlock',
		image: 'infoBlock',
	};
	// BE options shape: { value, label }
	// PQ option shape (V2Option): { key, label }
	// The Single/MultiSelect components use `opt.key` for both the React key
	// AND the persisted answer id — without this mapping every checkbox would
	// share the same `undefined` key, which makes `selected.includes(key)`
	// match every option as soon as one is checked.
	const adaptedOptions = Array.isArray(el.options)
		? el.options.map((opt: { value?: string; key?: string; label?: unknown; icon?: string; description?: unknown }) => ({
			key: opt.key ?? opt.value ?? '',
			// Defensive: option labels arrive as plain strings today, but unwrap
			// the localizable object shape too so a future BE change can't put an
			// object into a MenuItem/card and crash the renderer.
			label: unwrapLocalizableText(opt.label),
			...(opt.icon !== undefined ? { icon: opt.icon } : {}),
			...(opt.description !== undefined ? { description: unwrapLocalizableText(opt.description) } : {}),
		}))
		: [];
	// BE kind 'textarea' collapses to PQ type 'text' (same TextElement
	// component), but PQ's TextElement only renders as a multi-line area when
	// `multiline === true`. Forward the kind hint so authored `textarea` BE
	// elements get the taller, multi-row presentation the playbook intended.
	const multiline = el.kind === 'textarea' ? true : el.multiline === true;
	// `static_text` / `image` BE kinds map to PQ's InfoBlockElement, whose copy
	// lives in `body`. The BE carries that copy in `el.text` as a localizable
	// object (`{ format, value }`) — NOT in `el.label`. Unwrap it into `body`
	// so InfoBlockElement renders a string, never the raw object (the cause of
	// the "Objects are not valid as a React child {format, value}" crash).
	const isInfoBlock = el.kind === 'static_text' || el.kind === 'image';
	const body = isInfoBlock ? unwrapLocalizableText(el.text) : undefined;
	// `label` is the InfoBlock title (optional) and the input label everywhere
	// else. The BE serves it as a plain string; never fall back to `el.text`,
	// which is the localizable object shape and would crash the renderer.
	const label = unwrapLocalizableText(el.label);
	// Client-side validation wiring (RND-4282). The BE serves rules under
	// `validation` (singular array); the FE evaluator + renderers read
	// `validations` (plural), a top-level `required` boolean, and (for text)
	// a top-level `maxLength`. Without this rename none of those ever fire and
	// `validatePage` always returns valid → the client never gates a submit.
	const validations: any[] = Array.isArray(el.validation)
		? el.validation
		: Array.isArray(el.validations)
			? el.validations
			: [];
	const required = el.required === true || validations.some((r: any) => r?.kind === 'required');
	const maxLengthRule = validations.find((r: any) => r?.kind === 'maxLength');
	const maxLength =
		typeof el.maxLength === 'number'
			? el.maxLength
			: maxLengthRule?.value != null
				? Number(maxLengthRule.value)
				: undefined;
	// Server-backed (dynamic) option set (RND-4221): a NAICS-style element ships
	// `options: { dynamicOptionsType: "naics" }` (an OBJECT, not an array) because
	// its option list is too large to inline on the wire. There is no static
	// option array to coerce; instead route it to the searchable AutocompleteElement
	// which fetches matches on demand from /getDynamicOptions. We carry the
	// discriminator onto the adapted element and drop the `options` field entirely
	// so the static-dropdown renderer never sees an empty list and the V2 type stays
	// a clean V2AutocompleteElement. We still forward #491's client-side
	// `validations`/`required` so a NAICS question authored as required gates the
	// submit exactly like the static dropdown.
	// The discriminator arrives in TWO shapes depending on the BE path (RND-4269
	// goBack). The forward `page` response nests it as
	// `options: { dynamicOptionsType: "naics" }` (an OBJECT, not an array); the
	// `page/goBack` response FLATTENS it to a top-level `dynamicOptionsType:
	// "naics"` with no `options` wrapper at all. Accept both so a NAICS element
	// rewinds to the same searchable AutocompleteElement it was entered with —
	// otherwise goBack falls through to the static `select` → `dropdown` renderer
	// (an empty MUI <Select>) and the restored answer, though present in the
	// page's initialValues, never displays.
	const dynamicOptionsType =
		el.options && !Array.isArray(el.options) && typeof el.options.dynamicOptionsType === 'string'
			? el.options.dynamicOptionsType
			: typeof el.dynamicOptionsType === 'string'
				? el.dynamicOptionsType
				: undefined;
	if (dynamicOptionsType) {
		// Strip the raw `options` object (it isn't a V2SelectOption[]) so the
		// adapted element matches V2AutocompleteElement exactly.
		const { options: _ignored, ...rest } = el;
		// `allowFreeText` (RND-4217, address fields) rides the same dual wire
		// shape as `dynamicOptionsType`: nested under `options` on the forward
		// `page` response, flattened to top level on `page/goBack`. Accept both
		// so an address element rewinds with free-text behavior intact.
		const allowFreeText =
			el.allowFreeText === true ||
			(el.options && !Array.isArray(el.options) && el.options.allowFreeText === true);
		return {
			...rest,
			type: 'autocomplete',
			label,
			dynamicOptionsType,
			...(allowFreeText ? { allowFreeText: true } : {}),
			validations,
			required,
		};
	}
	return {
		...el,
		type: kindMap[el.kind] ?? el.kind ?? el.type ?? 'text',
		// PQ uses 'label' for display; BE uses 'label' too
		label,
		...(body !== undefined ? { body } : {}),
		options: adaptedOptions,
		multiline,
		validations,
		required,
		...(maxLength !== undefined ? { maxLength } : {}),
	};
}

/**
 * HRR Phase 1 fetch-based data hook. There is no react-query in PQ — see the
 * brief. The hook is intentionally stateless: callers (V2PageContext) own
 * the loading/error flags and decide when to retry.
 */
export function useQueryAnswerPage() {
	const base = useMemo(() => `${host}/api`, []);

	const fetchInvestigationDetails = useCallback(
		async (shortCode: string, signal?: AbortSignal): Promise<V2InvestigationDetails> => {
			// BE uses ?sc= query param, not /:sc path param
			const response = await fetch(
				`${base}/branchinvestigationdetails?sc=${encodeURIComponent(shortCode)}&lang=en`,
				{ method: 'GET', headers: { Accept: 'application/json' }, signal },
			);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const raw = await asJson<any>(response);
			// Adapt BE wire format → V2InvestigationDetails:
			// BE returns { nextQuestion: { qid, type, issuedPageVersion (UUID string), title, elements }, bankerOutreachText, account, status }
			// PQ expects { firstPageId, currentPage: V2Page, welcomeContext, done }
			const nq = raw?.nextQuestion;
			const currentPage: V2Page | undefined = nq?.type === 'page' ? {
				pageId: nq.qid ?? '',
				// BE uses UUID string; cast to any to avoid number mismatch — pageVersion used as opaque token
				pageVersion: nq.issuedPageVersion as unknown as number,
				title: nq.title ?? '',
				elements: (nq.elements ?? []).map(adaptElement),
			} : undefined;
			return {
				shortCode,
				firstPageId: nq?.qid ?? '',
				currentPage,
				welcomeContext: {
					firstName: raw?.account?.customerName ?? '',
					bankName: '',
					accountLast4: [],
				},
				done: raw?.status === 'completed',
			} as V2InvestigationDetails;
		},
		[base],
	);

	const submitAnswer = useCallback(
		async (
			pageId: string,
			payload: V2AnswerPagePayload,
			signal?: AbortSignal,
		): Promise<V2AnswerPageResponse> => {
			// BE endpoint: POST /page/answerPage?sc=XXXX
			// sc query param → middleware resolves caseId from shortlink
			// body: { questionId, pageVersion, values } per pageService.ts AnswerPageRequest
			const scParam = payload.sc ? `?sc=${encodeURIComponent(payload.sc)}` : '';
			const response = await fetch(`${base}/page/answerPage${scParam}`, {
				method: 'POST',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					questionId: pageId,
					pageVersion: payload.pageVersion,
					values: payload.answers,  // BE uses 'values' not 'answers'
					sc: payload.sc,  // BE middleware reads sc from POST body (not query) — serviceHttp.ts:100
				}),
				signal,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const raw = await asJson<any>(response);
			// Adapt BE response → V2AnswerPageResponse
			if (raw?.nextQuestion?.type === 'page') {
				const nq = raw.nextQuestion;
				return {
					done: false,
					nextPageId: nq.qid,
					nextPage: {
						pageId: nq.qid,
						pageVersion: nq.issuedPageVersion as unknown as number,
						title: nq.title ?? '',
						elements: (nq.elements ?? []).map(adaptElement),
					},
				} as V2AnswerPageResponse;
			}
			// Terminal signal. The v2 `answerPage` endpoint does NOT finalize the
			// case the way v1's `branchAnswerQuestion` does, so a `type: 'complete'`
			// response still needs an explicit `branchCompleteInvestigation` POST to
			// lock the case (RND-4277). Flag that with `needsComplete`. An already-
			// `completed` status (e.g. re-entry boot) is terminal but already locked.
			if (raw?.nextQuestion?.type === 'complete') {
				return { done: true, needsComplete: true } as V2AnswerPageResponse;
			}
			if (raw?.status === 'completed') {
				return { done: true, needsComplete: false } as V2AnswerPageResponse;
			}
			return raw as V2AnswerPageResponse;
		},
		[base],
	);

	/**
	 * Finalize the case server-side. The v2 `answerPage` terminal response only
	 * SIGNALS completion (`nextQuestion.type === 'complete'`) — it does not lock
	 * the case. Without this explicit call the RFI stays open: the customer can
	 * re-enter and resubmit, the consolidated Outreach Summary event is never
	 * emitted, and the list item never flips to unread/bolded (RND-4277).
	 *
	 * Body mirrors the established `branchCompleteInvestigation` contract used by
	 * the legacy `useQueryCompleteInvestigation` hook. Throws `V2RequestError` on
	 * a non-2xx so the context layer can surface an inline retry.
	 */
	const completeInvestigation = useCallback(
		async (sc?: string, signal?: AbortSignal): Promise<void> => {
			const response = await fetch(`${base}/branchCompleteInvestigation`, {
				method: 'POST',
				headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				body: JSON.stringify({
					answeredBy: INVESTIGATION_ANSWERED_BY.CLIENT,
					emotionalState: null,
					notes: '',
					...(sc ? { sc } : {}),
				}),
				signal,
			});
			await asJson<unknown>(response); // throws V2RequestError on non-2xx
		},
		[base],
	);

	const goBack = useCallback(
		async (pageId: string, signal?: AbortSignal, sc?: string): Promise<V2BackResponse> => {
			// sc query param → middleware resolves caseId from shortlink
			const scParam = sc ? `?sc=${encodeURIComponent(sc)}` : '';
			const response = await fetch(`${base}/page/goBack${scParam}`, {
				method: 'POST',
				headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				body: JSON.stringify({ questionId: pageId, sc }),
				signal,
			});
			const raw = await asJson<any>(response); // eslint-disable-line @typescript-eslint/no-explicit-any
			const nq = raw?.nextQuestion;
			if (nq?.type === 'page') {
				return {
					allowed: true,
					previousPageId: nq.qid,
					previousPage: {
						pageId: nq.qid,
						pageVersion: nq.issuedPageVersion as unknown as number,
						title: nq.title ?? '',
						elements: (nq.elements ?? []).map(adaptElement),
					},
					// Surface BE-returned prior answers so the FE can re-populate
					// the page with what the customer previously entered. BE
					// shape: { [elementId]: value }.
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					initialValues: (nq.initialValues ?? {}) as Record<string, any>,
				} as V2BackResponse;
			}
			return raw as V2BackResponse;
		},
		[base],
	);

	const uploadFile = useCallback(
		async (
			pageId: string,
			elementId: string,
			file: File,
			signal?: AbortSignal,
		): Promise<V2UploadResponse> => {
			const form = new FormData();
			form.append('elementId', elementId);
			form.append('file', file, file.name);

			const response = await fetch(`${base}/page/${encodeURIComponent(pageId)}/upload`, {
				method: 'POST',
				body: form,
				signal,
			});
			return asJson<V2UploadResponse>(response);
		},
		[base],
	);

	return {
		fetchInvestigationDetails,
		submitAnswer,
		completeInvestigation,
		goBack,
		uploadFile,
	};
}

export type UseQueryAnswerPage = ReturnType<typeof useQueryAnswerPage>;
